import { fetchConfig } from '@theme/utilities';

/**
 * Automatically applies a discount code from the URL `code` query parameter to the cart.
 * The code is also persisted in sessionStorage so it survives page navigations
 * before the cart is loaded (e.g. browsing from a landing page to the cart).
 *
 * Usage: /collections/beard-growth-kits?code=GROWCLUB
 */

const URL_PARAM = 'code';
const STORAGE_KEY = 'url_discount_code';

/**
 * Reads the discount code from the URL search params.
 * @returns {string | null}
 */
function getCodeFromURL() {
  const params = new URLSearchParams(window.location.search);
  return params.get(URL_PARAM);
}

/**
 * Removes the `code` query parameter from the URL without reloading the page.
 */
function cleanURL() {
  const url = new URL(window.location.href);
  url.searchParams.delete(URL_PARAM);
  window.history.replaceState(window.history.state, document.title, url.toString());
}

/**
 * Applies a discount code to the current cart via the Shopify AJAX Cart API.
 * Skips application if the code is already present on the cart.
 *
 * @param {string} code - The discount code to apply.
 * @returns {Promise<boolean>} Whether the code was successfully applied.
 */
async function applyDiscount(code) {
  try {
    // Fetch the current cart to check existing discount codes
    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
    const cart = await cartResponse.json();

    const existingCodes = (cart.discount_codes || []).map(
      (/** @type {{ code: string }} */ d) => d.code
    );

    // Already applied — nothing to do
    if (existingCodes.includes(code)) return true;

    const allCodes = [...existingCodes, code].join(',');

    const config = fetchConfig('json', {
      body: JSON.stringify({ discount: allCodes }),
    });

    const response = await fetch(Theme.routes.cart_update_url, config);
    const data = await response.json();

    const applied = data.discount_codes?.find(
      (/** @type {{ code: string; applicable: boolean }} */ d) => d.code === code
    );

    if (!applied || !applied.applicable) {
      console.warn(`[url-discount] Code "${code}" was not applicable.`);
      return false;
    }

    document.dispatchEvent(new CustomEvent('discount:applied', { detail: { code } }));

    return true;
  } catch (error) {
    console.error('[url-discount] Failed to apply discount code:', error);
    return false;
  }
}

/**
 * Main entry — runs once on page load.
 * 1. If a `code` param is in the URL, store it and clean the URL.
 * 2. If a stored code exists, attempt to apply it to the cart.
 */
async function init() {
  const urlCode = getCodeFromURL();

  if (urlCode) {
    sessionStorage.setItem(STORAGE_KEY, urlCode);
    cleanURL();
  }

  const code = sessionStorage.getItem(STORAGE_KEY);
  if (!code) return;

  const success = await applyDiscount(code);

  // Clear storage once successfully applied so we don't re-apply on every page
  if (success) {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

init();
